import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import { get, put, post } from '../api';
import { Button } from '../components/Button';
import {
  ArrowDownTrayIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ArrowLeftIcon,
  SparklesIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import '@google/model-viewer';

export default function AiStudioModelDetailsPage() {
  const { jobId } = useParams({ strict: false });
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Editing Title
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);

  // Regeneration
  const [prompt, setPrompt] = useState('');

  // Version Dropdown
  const [isVersionOpen, setIsVersionOpen] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  // Lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    if (jobId) {
      setIsRegenerating(false);
      setPrompt('');
      fetchJob();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // Poll for updates if job is processing
  useEffect(() => {
    if (!job || ['SUCCESS', 'FAILED'].includes(job.status)) return;

    const pollInterval = setInterval(async () => {
      try {
        const data = await get(`/api/1/generate/job/${jobId}`);
        setJob(data);
      } catch (err) {
        console.error('Polling failed:', err);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.status, jobId]);

  const fetchJob = async () => {
    setLoading(true);
    try {
      const data = await get(`/api/1/generate/job/${jobId}`);
      setJob(data);
      setEditedTitle(data.name || 'Untitled Model');

      // If the job failed, pre-fill the prompt so the user can easily retry
      if (data.status === 'FAILED' && data.prompt) {
        setPrompt(data.prompt);
      }
    } catch (err) {
      console.error('Failed to fetch job:', err);
      setError('Failed to load model details.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTitle = async () => {
    if (!editedTitle.trim()) return;

    setIsSavingTitle(true);
    try {
      await put(`/api/1/generate/job/${jobId}`, { name: editedTitle });
      setJob(prev => ({ ...prev, name: editedTitle }));
      setIsEditingTitle(false);
    } catch (err) {
      console.error('Failed to update title:', err);
      // Optionally show toast
    } finally {
      setIsSavingTitle(false);
    }
  };

  const handleRegenerate = async () => {
    if (!prompt.trim()) return;

    setIsRegenerating(true);
    try {
      const response = await post(`/api/1/generate/job/${jobId}/regenerate`, {
        prompt,
        settings: job.settings || {}
      });

      if (response.job_id) {
        setPrompt(''); // Reset instructions
        navigate({ to: `/app/ai-studio/model/${response.job_id}` });
      }
    } catch (err) {
      console.error('Failed to regenerate:', err);
      alert('Failed to start regeneration.');
      setIsRegenerating(false); // Only re-enable on error, otherwise keep disabled while navigating/loading
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-10 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-10 text-center">
        <p className="text-danger-foreground mb-4">{error || 'Job not found'}</p>
        <Button variant="secondary" onClick={() => navigate({ to: '/app/ai-studio/models' })}>
          Back to models
        </Button>
      </div>
    );
  }

  const allImages = [
    ...(job.reference_image_urls || []),
    ...(job.generated_image_urls || []),
    job.generated_image_url
  ].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i); // Unique images

  return (
    <div className="max-w-[1600px] mx-auto px-6 py-8 font-sans h-full flex flex-col">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-6 shrink-0">
        <div className="flex items-center gap-6">
          <button
            onClick={() => navigate({ to: '/app/ai-studio/models' })}
            className="p-3 hover:bg-foreground/5 rounded-full transition-colors border border-transparent hover:border-border/50"
          >
            <ArrowLeftIcon className="w-5 h-5 text-muted-foreground" />
          </button>

          {isEditingTitle ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                className="bg-transparent border-b-2 border-foreground/20 px-0 py-1 text-2xl font-bold uppercase tracking-tight focus:outline-none focus:border-purple-500 w-full max-w-md"
                autoFocus
              />
              <button
                onClick={handleSaveTitle}
                disabled={isSavingTitle}
                className="p-2 hover:text-green-500 transition-colors"
              >
                <CheckIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => {
                  setIsEditingTitle(false);
                  setEditedTitle(job.name || '');
                }}
                className="p-2 hover:text-red-500 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-4 group">
              <h1 className="text-3xl font-black tracking-tighter text-foreground uppercase">
                {job.name || 'Untitled Asset'}
              </h1>
              <button
                onClick={() => setIsEditingTitle(true)}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:bg-foreground/5 rounded-full text-muted-foreground"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4">
          {job.status === 'SUCCESS' && job.generated_model_url && (
            <a
              href={job.generated_model_url}
              download
              className="inline-flex items-center gap-2 px-6 py-3 bg-foreground text-background hover:bg-foreground/90 rounded-full text-xs font-bold uppercase tracking-wider transition-all shadow-lg hover:shadow-xl"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              Download
            </a>
          )}

          {/* Version Selector */}
          {job.versions && job.versions.length > 1 && (
            <div className="relative z-50">
              <button
                onClick={() => setIsVersionOpen(!isVersionOpen)}
                className="flex items-center gap-3 px-4 py-2 bg-background/50 backdrop-blur-sm border border-border/50 rounded-lg hover:bg-background/80 transition-all group"
              >
                <div className="flex flex-col items-start text-left">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Version</span>
                  <span className="text-sm font-mono font-bold text-foreground">v{job.version}</span>
                </div>
                <ChevronDownIcon className={`w-4 h-4 text-muted-foreground group-hover:text-foreground transition-transform duration-300 ${isVersionOpen ? 'rotate-180' : ''}`} />
              </button>

              {isVersionOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsVersionOpen(false)}
                  />
                  <div className="absolute top-full right-0 mt-2 w-48 bg-zinc-950 border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50 flex flex-col animate-in fade-in zoom-in-95 duration-200">
                    <div className="max-h-[300px] overflow-y-auto">
                      {[...job.versions].sort((a, b) => b.version - a.version).map((v) => (
                        <button
                          key={v.id}
                          onClick={() => {
                            navigate({ to: `/app/ai-studio/model/${v.id}` });
                            setIsVersionOpen(false);
                          }}
                          className={`w-full flex items-center justify-between px-4 py-3 text-xs font-mono transition-all border-b border-white/5 last:border-0 ${v.id === job.id
                              ? 'bg-white/10 text-white font-bold'
                              : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                            }`}
                        >
                          <span>v{v.version}</span>
                          {v.id === job.id && (
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 flex-grow min-h-0">
        {/* Left Column: 3D Viewer */}
        <div className="lg:col-span-8 flex flex-col gap-6 min-h-0">
          <div className="bg-black/5 dark:bg-white/5 rounded-3xl border border-border/50 overflow-hidden relative shadow-2xl flex-grow min-h-[500px]">
            {/* Technical markers */}
            <div className="absolute top-6 left-6 text-[10px] font-mono text-muted-foreground/50 pointer-events-none z-10">
              VIEWPORT_01
            </div>
            <div className="absolute bottom-6 right-6 text-[10px] font-mono text-muted-foreground/50 pointer-events-none z-10">
              RENDER_ENGINE_V1
            </div>
            <div className="absolute top-1/2 left-4 w-1 h-12 bg-foreground/10 rounded-full pointer-events-none z-10"></div>
            <div className="absolute bottom-4 left-1/2 w-12 h-1 bg-foreground/10 rounded-full pointer-events-none z-10"></div>

            {job.status === 'SUCCESS' && job.generated_model_url ? (
              <model-viewer
                src={job.generated_model_url}
                alt={job.name || "3D Model"}
                auto-rotate
                camera-controls
                shadow-intensity="1"
                shadow-softness="1"
                environment-image="neutral"
                exposure="1.0"
                style={{ width: '100%', height: '100%' }}
                poster={job.generated_image_urls?.[0] || job.generated_image_url}
              >
                <div slot="poster" className="flex items-center justify-center w-full h-full bg-background-soft">
                  <div className="w-16 h-16 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin"></div>
                </div>
              </model-viewer>
            ) : (
              <div className="w-full h-full relative bg-background-soft">
                {/* Background Image with Blur */}
                {(job.generated_image_urls?.[0] || job.generated_image_url) && (
                  <img
                    src={job.generated_image_urls?.[0] || job.generated_image_url}
                    alt="Reference"
                    className="w-full h-full object-cover blur-xl opacity-30 scale-110"
                  />
                )}

                {/* Overlay Content */}
                <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center z-10">
                  {job.status === 'FAILED' ? (
                    <div className="bg-red-500/10 backdrop-blur-md px-8 py-6 rounded-2xl border border-red-500/20 flex flex-col items-center gap-4 max-w-md">
                      <XMarkIcon className="w-10 h-10 text-red-500" />
                      <div>
                        <p className="font-bold text-red-500 uppercase tracking-widest text-sm">Generation Failed</p>
                        <p className="text-sm text-red-400/80 mt-2 font-mono">{job.error_message}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-background/40 backdrop-blur-xl px-8 py-6 rounded-2xl border border-white/10 flex flex-col items-center gap-4">
                      <div className="relative">
                        <div className="w-16 h-16 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
                        <SparklesIcon className="w-6 h-6 text-purple-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                      </div>
                      <span className="font-mono text-xs uppercase tracking-widest text-foreground">
                        {job.status === 'PENDING' && 'Queued...'}
                        {job.status === 'GENERATING_IMAGE' && 'Generating image...'}
                        {job.status === 'IMAGE_READY' && 'Image ready...'}
                        {job.status === 'GENERATING_3D' && 'Generating 3D model...'}
                        {!['PENDING', 'GENERATING_IMAGE', 'IMAGE_READY', 'GENERATING_3D'].includes(job.status) && 'Processing...'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Image Gallery */}
          {allImages.length > 0 && (
            <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
              {allImages.map((url, idx) => (
                <div
                  key={idx}
                  className="w-24 h-24 shrink-0 rounded-xl overflow-hidden border border-border/50 bg-background-soft group relative cursor-pointer hover:ring-2 ring-purple-500 transition-all"
                  onClick={() => {
                    setLightboxIndex(idx);
                    setLightboxOpen(true);
                  }}
                >
                  <img src={url} alt={`View ${idx + 1}`} className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Column: Edit / Regenerate */}
        <div className="lg:col-span-4 space-y-6 overflow-y-auto pr-2">
          <div className="bg-background/30 backdrop-blur-sm rounded-3xl border border-border/50 p-8 shadow-lg">
            <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-6 flex items-center gap-2">
              <SparklesIcon className="w-4 h-4 text-purple-500" />
              Refine Model
            </h3>

            <div className="space-y-6">
              <div className="relative group">
                <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-2 group-focus-within:text-purple-500 transition-colors">
                  Prompt Instructions
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="w-full bg-background-soft/50 border border-border rounded-xl p-4 text-sm focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 min-h-[120px] resize-y transition-all placeholder:text-muted-foreground/30"
                  placeholder="Describe changes to the model..."
                />
              </div>

              <Button
                variant="primary"
                className="w-full justify-center py-4 text-xs font-bold uppercase tracking-widest shadow-lg shadow-purple-500/10"
                onClick={handleRegenerate}
                disabled={isRegenerating || !prompt.trim()}
              >
                {isRegenerating ? 'Processing...' : 'Regenerate Version'}
              </Button>

              <p className="text-[10px] text-muted-foreground text-center font-mono">
                CREATES NEW VERSION v{job.version + 1}
              </p>
            </div>
          </div>

          {/* Job Details */}
          <div className="bg-background/30 backdrop-blur-sm rounded-3xl border border-border/50 p-8">
            <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-6">
              Asset Data
            </h3>
            <dl className="space-y-4 text-sm font-mono">
              <div className="flex justify-between items-center border-b border-border/30 pb-2">
                <dt className="text-muted-foreground text-xs uppercase">Status</dt>
                <dd className={`font-bold text-xs uppercase px-2 py-1 rounded ${job.status === 'SUCCESS' ? 'bg-green-500/10 text-green-500' :
                    job.status === 'FAILED' ? 'bg-red-500/10 text-red-500' :
                      'bg-purple-500/10 text-purple-500'
                  }`}>
                  {job.status}
                </dd>
              </div>
              <div className="flex justify-between items-center border-b border-border/30 pb-2">
                <dt className="text-muted-foreground text-xs uppercase">Created</dt>
                <dd className="text-foreground text-xs">
                  {new Date(job.created_at).toLocaleDateString()}
                </dd>
              </div>
              <div className="flex justify-between items-center border-b border-border/30 pb-2">
                <dt className="text-muted-foreground text-xs uppercase">Type</dt>
                <dd className="text-foreground text-xs">
                  {job.generation_type === 'TEXT_TO_3D' ? 'Text to 3D' : 'Image to 3D'}
                </dd>
              </div>
              {job.credits_used > 0 && (
                <div className="flex justify-between items-center border-b border-border/30 pb-2">
                  <dt className="text-muted-foreground text-xs uppercase">Cost</dt>
                  <dd className="text-foreground text-xs">{job.credits_used} CR</dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-xl" onClick={() => setLightboxOpen(false)}>
          <button
            className="absolute top-6 right-6 p-2 text-white/50 hover:text-white transition-colors"
            onClick={() => setLightboxOpen(false)}
          >
            <XMarkIcon className="w-10 h-10" />
          </button>

          <div className="relative max-w-7xl max-h-[90vh] w-full px-4 flex items-center justify-center" onClick={e => e.stopPropagation()}>
            <img
              src={allImages[lightboxIndex]}
              alt={`View ${lightboxIndex + 1}`}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />

            {allImages.length > 1 && (
              <>
                <button
                  className="absolute left-4 p-4 bg-white/5 hover:bg-white/10 text-white rounded-full transition-colors backdrop-blur-md border border-white/10"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
                  }}
                >
                  <ArrowLeftIcon className="w-6 h-6" />
                </button>
                <button
                  className="absolute right-4 p-4 bg-white/5 hover:bg-white/10 text-white rounded-full transition-colors backdrop-blur-md border border-white/10"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
                  }}
                >
                  <ArrowLeftIcon className="w-6 h-6 rotate-180" />
                </button>
              </>
            )}
          </div>

          <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-3">
            {allImages.map((_, idx) => (
              <button
                key={idx}
                className={`h-1 rounded-full transition-all duration-300 ${idx === lightboxIndex ? 'bg-white w-8' : 'bg-white/20 w-4 hover:bg-white/40'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex(idx);
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
